import { BadgeProps, borderRadius, color, padding, shadow } from '@evrekadev/evreka-ui-components'
import React, { PropsWithChildren, ReactNode, useState } from 'react'
import styled, { css } from 'styled-components'
import { Typography, Flex, Badge } from 'components'
import get from 'lodash-es/get'

const TempTabsContainer = styled.div<{ fullWidth?: boolean }>`
  ${padding([4])}
  ${borderRadius(8)}
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'fit-content')};
  border: 1px solid ${color('grey100')};
  background-color: ${color('grey50')};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`
const TempTab = styled.div<{ isSelected?: boolean }>`
  ${padding([2, 8])}
  ${borderRadius(6)}
  user-select: none;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
  flex-grow: 1;

  ${(props) =>
    props.isSelected &&
    css`
      ${shadow('sm')}
      background-color: ${color('white')};
    `}
`

// this turns:
// [<> [child, child] </>]
// into:
// [child, child]
const flattenChildren = (children: ReactNode): ReactNode[] => {
  const flatChildren: ReactNode[] = []
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === React.Fragment) {
        flatChildren.push(...flattenChildren(child.props.children))
      } else {
        flatChildren.push(child)
      }
    }
  })
  return flatChildren
}

export const TabsV2: TabComponent = ({
  children,
  selectedIndex = 0,
  onSelect,
  right,
  fullWidth,
  fullHeight = true,
  style,
}) => {
  const isControlled = selectedIndex != null && onSelect != null
  const [selectedTab, setSelectedTab] = useState<number>(selectedIndex)

  const selectedItem = isControlled ? selectedIndex : selectedTab
  const handleSelect = isControlled ? onSelect : setSelectedTab

  const flatChildren = flattenChildren(children)

  const tabButtons = flatChildren?.filter(
    (child) => React.isValidElement(child) && get(child, 'type.displayName', '') === 'TabButton',
  ) as Array<React.ReactElement<PropsWithChildren<TabButtonProps>>>

  const tabChildren = flatChildren?.filter(
    (child) => React.isValidElement(child) && get(child, 'type.displayName', '') === 'TabChild',
  ) as Array<React.ReactElement<PropsWithChildren>>

  return (
    <Flex
      directionColumn="zero"
      gap={0.75}
      fullHeight={fullHeight}
      fullWidth={fullWidth}
      style={style}
    >
      <Flex middle="zero" between="zero" gap={0.5} style={{ flexShrink: 0, overflow: 'auto' }}>
        <TempTabsContainer data-testid="tab-container" fullWidth={fullWidth}>
          {tabButtons?.map((tabButton, i) => (
            <TempTab
              key={i}
              isSelected={selectedItem === i}
              onClick={() => handleSelect(i)}
              data-testid={`tab-button-${i}`}
            >
              <Typography
                fontSize="text_sm"
                fontWeight="semibold"
                color={selectedItem === i ? 'grey700' : 'grey500'}
              >
                {tabButton?.props.children}
              </Typography>
              {tabButton.props.badge && (
                <Badge color={tabButton.props.badge.color}>
                  {tabButton.props.badge.loading ? '...' : tabButton.props.badge.content}
                </Badge>
              )}
            </TempTab>
          ))}
        </TempTabsContainer>
        {right}
      </Flex>

      {tabChildren && tabChildren.length > 0 && (
        <div style={{ flexGrow: 1, overflow: 'auto' }}>
          {tabChildren[selectedItem].props.children}
        </div>
      )}
    </Flex>
  )
}

type TabComponent = React.FC<
  PropsWithChildren<{
    // children?: JSX.Element[] | React.ReactNode
    selectedIndex?: number
    onSelect?: (index: number) => void
    right?: React.ReactNode
    fullWidth?: boolean
    fullHeight?: boolean
    style?: React.CSSProperties
  }>
> & {
  TabChild: React.FC<React.PropsWithChildren>
  TabButton: React.FC<React.PropsWithChildren<TabButtonProps>>
}

type TabButtonProps = {
  badge?: {
    content: string
    color: BadgeProps['color']
    loading?: boolean
  }
}

const TabChild: React.FC<React.PropsWithChildren<{}>> = () => <></>
const TabButton: React.FC<React.PropsWithChildren<TabButtonProps>> = () => <></>

TabChild.displayName = 'TabChild'

TabButton.displayName = 'TabButton'

TabsV2.TabChild = TabChild
TabsV2.TabButton = TabButton
